import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { convertBrandingToManorTheme, isValidPayload, brandTheme, brandContent } from 'ctm-util-functions/lib/branding';
import { pageContext, logToNR } from 'ctm-util-functions';
import { getBrand, getBrandingServiceUrl, getEnv } from '../store/selectors';
import { themes as localFiles } from '../themes/themes';

const getBrandThemeLocalFile = (brandcode) => localFiles[brandcode];
const isLocalOrDev = (env) => (env === 'local' || env === 'dev');

export const useBrandingService = () => {
    const dispatch = useDispatch();

    const [manorTheme, setManorTheme] = useState(null);
    const isBrandThemeSet = !!manorTheme;
    const [brandingServiceApiResponse, setBrandingServiceApiResponse] = useState(undefined);

    const brandCode = useSelector(getBrand);
    const environment = useSelector(getEnv);
    const brandingServiceUrl = useSelector(getBrandingServiceUrl);
    const vertical = pageContext.getVertical();

    const switchTheme = (brandThemes) => {
        // Target the root element to set css vars
        const root = document.querySelector(':root');
        const theme = brandTheme(brandThemes);

        if (theme) {
            Object.keys(theme).forEach((key) => {
                const styleKey = `--${key.replace(/_/g, '-')}`;
                root.style.setProperty(styleKey, theme[key]);
            });
            setManorTheme(convertBrandingToManorTheme(theme));
        } else {
            logToNR('Branding Service no brand theme', 'ERROR', vertical);
            throw new Error('Error with brand themes during theme switch');
        }

        const content = brandContent(vertical, brandThemes);
        if (!content) {
            logToNR('Branding Service no brand content', 'ERROR');
            throw new Error(`No brand content found for the vertical ${vertical} for brand ${brandCode}`);
        }

        dispatch({
            type: 'SET_CONTENT',
            value: content
        });
    };

    useEffect(() => {
        if (brandCode) {
            if ((!isBrandThemeSet || isLocalOrDev(environment))) {
                axios
                    .get(`${brandingServiceUrl}${brandCode}`, { timeout: 1000 })
                    .then((response) => {
                        const payload = response.data;
                        if (isValidPayload(vertical, payload)) {
                            setBrandingServiceApiResponse(payload);
                        } else {
                            logToNR('Branding Service Payload invalid', 'WARN');
                            const brandThemeFile = getBrandThemeLocalFile(brandCode);
                            setBrandingServiceApiResponse(brandThemeFile);
                        }
                    })
                    .catch((error) => {
                        if (error.code === 'ECONNABORTED') {
                            logToNR('Branding Service call timeout', 'ERROR');
                        } else {
                            logToNR(`Branding Service call error: ${error.message}`, 'ERROR');
                        }

                        const brandThemeFile = getBrandThemeLocalFile(brandCode);
                        setBrandingServiceApiResponse(brandThemeFile);
                    });
            }
        }
    }, [brandCode]);

    // Set the theme based on a brandcode and branding service response being available
    useEffect(() => {
        if (brandingServiceApiResponse) {
            switchTheme(brandingServiceApiResponse);
        }
    }, [brandingServiceApiResponse]);

    return { isBrandThemeSet, manorTheme };
};
